import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioButton } from '@angular/material/radio';
import { MatTooltipModule } from '@angular/material/tooltip';
import { connectState } from '@examdojo/angular/util';
import { ButtonComponent } from '@examdojo/core/button';
import { IconButtonComponent } from '@examdojo/core/icon-button';

import { isNullish } from '@examdojo/core/util/nullish';

import { QuestionEditingOptions } from '@examdojo/models/question-editor';
import { ContentItemEditorComponent, ContentItemFormGroup } from '@examdojo/question-content-item';
import { NumberInputComponent } from '@examdojo/ui/input';
import { startWith, switchMap } from 'rxjs';
import {
  ActiveQuestionEditorSubItemOptionsCoordinates,
  createOptionFormGroup,
  OptionsItemFormGroup,
} from '../question-item-editor.model';
import { OptionLabelComponent } from './option-label.component';

@Component({
  selector: 'dojo-question-item-options-editor',
  imports: [
    ContentItemEditorComponent,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatRadioButton,
    MatIconModule,
    IconButtonComponent,
    ButtonComponent,
    MatTooltipModule,
    NumberInputComponent,

    OptionLabelComponent,
  ],
  host: {
    class: 'flex h-full flex-col px-2',
  },
  styleUrls: ['./question-item-options-editor.component.scss'],
  templateUrl: './question-item-options-editor.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuestionItemOptionsEditorComponent {
  readonly optionsFormGroup = input.required<OptionsItemFormGroup>();
  readonly editingOptions = input.required<QuestionEditingOptions>();
  readonly allowQuestionStructureEditing = input.required<boolean>();
  readonly editItem = output<{
    item: ContentItemFormGroup;
    coordinates: ActiveQuestionEditorSubItemOptionsCoordinates;
  }>();

  readonly correctOptionIndexesValue$ = toObservable(this.optionsFormGroup).pipe(
    switchMap((formCtrl) =>
      formCtrl.controls.markscheme.controls.correct_option_indexes.valueChanges.pipe(
        startWith(formCtrl.controls.markscheme.controls.correct_option_indexes.value),
      ),
    ),
  );

  readonly state = connectState({
    correctOptionIndexesValue: this.correctOptionIndexesValue$,
  });

  editOptionSubItem(
    { item, contentItemIndex }: { item: ContentItemFormGroup; contentItemIndex: number },
    optionsIndex?: number,
  ) {
    this.editItem.emit({
      item,
      coordinates: isNullish(optionsIndex)
        ? { content_items: contentItemIndex }
        : { content_items: contentItemIndex, options: optionsIndex },
    });
  }

  addOption() {
    const newOption = createOptionFormGroup(undefined, this.editingOptions());
    this.optionsFormGroup().controls.options.push(newOption);
    this.optionsFormGroup().controls.options.markAsDirty();
    this.optionsFormGroup().controls.options.markAsTouched();
  }

  removeOption(index: number) {
    this.optionsFormGroup().controls.options.removeAt(index);
    this.optionsFormGroup().controls.options.markAsDirty();
    this.optionsFormGroup().controls.options.markAsTouched();
  }

  toggleCorrectOption(index: number) {
    this.optionsFormGroup().controls.markscheme.controls.correct_option_indexes.setValue([index]);
    this.optionsFormGroup().updateValueAndValidity();
    this.optionsFormGroup().markAsDirty();
    this.optionsFormGroup().markAsTouched();
  }

  editWorkedSolutionItem({ item, contentItemIndex }: { item: ContentItemFormGroup; contentItemIndex: number }) {
    this.editItem.emit({
      item,
      coordinates: { content_items: contentItemIndex, worked_solution: true },
    });
  }
}
